import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  cancelProviderPayment,
  listProviderDirectory,
  listProviderPayments,
  registerProviderPayment,
} from "@/lib/provider-payments.functions";
import { listProviderCortes } from "@/lib/provider-settlement.functions";
import { listExternalBookings } from "@/lib/external-bookings.functions";
import { CURRENCIES, money, type Currency } from "@/lib/provider-schemas";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/_authenticated/admin/provider-payments")({
  head: () => ({
    meta: [
      { title: "Pagos a proveedores — Administración" },
      { name: "description", content: "Registro de pagos totales, parciales y agrupados a proveedores." },
      { property: "og:title", content: "Pagos a proveedores — Administración · Mostrando México" },
      { property: "og:description", content: "Administración privada de proveedores por empresa." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Pagos a proveedores"
      description="Registra pagos totales, parciales o agrupados (un pago que cubre varias reservas) y consulta el historial."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

const providersQuery = queryOptions({
  queryKey: ["admin", "provider-directory"],
  queryFn: () => listProviderDirectory(),
});

function Body() {
  const qc = useQueryClient();
  const { data: dir } = useSuspenseQuery(providersQuery);
  const [providerId, setProviderId] = useState("");
  const [currency, setCurrency] = useState<Currency>("MXN");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedCortes, setSelectedCortes] = useState<Record<string,number>>({});
  const cortes = useSuspenseQuery(queryOptions({queryKey:["admin","provider-cortes"],queryFn:()=>listProviderCortes()}));
  const [selected, setSelected] = useState<Record<string, number>>({});

  const pending = useSuspenseQuery(
    queryOptions({
      queryKey: ["admin", "payables-open", providerId, currency],
      queryFn: () =>
        providerId
          ? listExternalBookings({
              data: { providerId, currency, limit: 500 } as never,
            })
          : Promise.resolve(null),
    }),
  );

  const history = useSuspenseQuery(
    queryOptions({
      queryKey: ["admin", "provider-payments", providerId],
      queryFn: () => listProviderPayments({ data: { providerId: providerId || null } }),
    }),
  );

  const openRows = useMemo(
    () =>
      (pending.data?.bookings ?? []).filter(
        (b: any) =>
          Number(b.pending_amount ?? 0) > 0 &&
          !["cancelada", "reembolsada"].includes(String(b.booking_status)),
      ),
    [pending.data],
  );

  const allocated = [...Object.values(selected),...Object.values(selectedCortes)].reduce((a, x) => a + (Number(x) || 0), 0);

  const register = useMutation({
    mutationFn: () =>
      registerProviderPayment({
        data: {
          provider_id: providerId,
          paid_at: paidAt,
          amount,
          currency,
          method: method || null,
          reference: reference || null,
          bank_account: bankAccount || null,
          notes: notes || null,
          corte_allocations: Object.entries(selectedCortes).filter(([,v])=>v>0).map(([corte_id,amount])=>({corte_id,amount})),
          allocations: Object.entries(selected)
            .filter(([, v]) => Number(v) > 0)
            .map(([external_booking_id, v]) => ({ external_booking_id, amount: Number(v) })),
        },
      }),
    onSuccess: (res) => {
      toast.success(
        res.difference
          ? `Pago registrado con diferencia de ${money(res.difference, currency)}`
          : "Pago registrado y aplicado",
      );
      setSelected({});
      setSelectedCortes({});
      setAmount(0);
      setReference("");
      setNotes("");
      qc.invalidateQueries({ queryKey: ["admin", "provider-payments"] });
      qc.invalidateQueries({ queryKey: ["admin", "payables-open"] });
      qc.invalidateQueries({ queryKey: ["admin", "provider-dashboard"] });
      qc.invalidateQueries({ queryKey: ["admin", "provider-directory"] });
      qc.invalidateQueries({ queryKey: ["admin", "external-bookings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const voidPay = useMutation({
    mutationFn: (id: string) => cancelProviderPayment({ data: { id } }),
    onSuccess: () => {
      toast.success("Pago anulado");
      qc.invalidateQueries({ queryKey: ["admin", "provider-payments"] });
      qc.invalidateQueries({ queryKey: ["admin", "payables-open"] });
      qc.invalidateQueries({ queryKey: ["admin", "provider-dashboard"] });
      qc.invalidateQueries({ queryKey: ["admin", "provider-directory"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function autoDistribute() {
    let left = amount;
    const next: Record<string, number> = {};
    for (const b of openRows as any[]) {
      if (left <= 0) break;
      const take = Math.min(left, Number(b.pending_amount));
      next[String(b.id)] = Number(take.toFixed(2));
      left -= take;
    }
    setSelected(next);
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Importe del pago" value={money(amount, currency)} />
        <StatCard label="Distribuido" value={money(allocated, currency)} />
        <StatCard
          label="Diferencia"
          value={<span className={amount - allocated ? "text-destructive" : ""}>{money(amount - allocated, currency)}</span>}
        />
      </div>

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-3 font-semibold">Registrar pago</h2>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Field label="Proveedor *">
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={providerId}
              onChange={(e) => {
                setProviderId(e.target.value);
                setSelected({});
                setSelectedCortes({});
              }}
            >
              <option value="">Selecciona…</option>
              {dir.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fecha del pago">
            <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
          </Field>
          <Field label="Importe">
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value) || 0)}
            />
          </Field>
          <Field label="Moneda">
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={currency}
              onChange={(e) => {
                setCurrency(e.target.value as Currency);
                setSelected({});
                setSelectedCortes({});
              }}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Método">
            <Input
              placeholder="Transferencia, efectivo…"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            />
          </Field>
          <Field label="Cuenta / banco">
            <Input value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} />
          </Field>
          <Field label="Referencia / comprobante">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          <div className="sm:col-span-3 lg:col-span-4">
            <Field label="Notas">
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">
              Reservas con saldo ({openRows.length}) — {currency}
            </h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!amount || !openRows.length} onClick={autoDistribute}>
                Distribuir automáticamente
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected({})}>
                Limpiar selección
              </Button>
            </div>
          </div>
          <div className="max-h-80 overflow-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/70 text-left uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-2 py-2" />
                  <th className="px-2 py-2">Servicio</th>
                  <th className="px-2 py-2">Tour</th>
                  <th className="px-2 py-2">Cliente</th>
                  <th className="px-2 py-2 text-right">Costo</th>
                  <th className="px-2 py-2 text-right">Pendiente</th>
                  <th className="px-2 py-2 text-right">Aplicar</th>
                </tr>
              </thead>
              <tbody>
                {openRows.map((b: any) => {
                  const checked = selected[b.id] != null;
                  return (
                    <tr key={b.id} className="border-t">
                      <td className="px-2 py-1">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = { ...selected };
                            if (v) next[b.id] = Number(Number(b.pending_amount).toFixed(2));
                            else delete next[b.id];
                            setSelected(next);
                          }}
                        />
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">{b.service_date}</td>
                      <td className="px-2 py-1">{b.tour_title}</td>
                      <td className="px-2 py-1">
                        {b.customer_first_name} {b.customer_last_name}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {money(b.provider_cost, b.cost_currency)}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {money(b.pending_amount, b.cost_currency)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <Input
                          className="h-8 w-24 text-right"
                          type="number"
                          value={selected[b.id] ?? ""}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            const next = { ...selected };
                            if (!v) delete next[b.id];
                            else next[b.id] = v;
                            setSelected(next);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
                {!openRows.length ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-6 text-center text-muted-foreground">
                      {providerId
                        ? "Este proveedor no tiene saldo pendiente en esta moneda."
                        : "Selecciona un proveedor para ver sus reservas por pagar."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className="mt-4 space-y-2"><h3 className="font-semibold">Asignar transferencia a cortes completos</h3>{cortes.data.cortes.filter((c:any)=>c.provider_id===providerId).map((c:any)=><label key={c.id} className="flex items-center gap-3 text-sm"><span>{c.period_start} — {c.period_end}</span><Input aria-label={`Asignar al corte ${c.id}`} type="number" className="w-32" value={selectedCortes[c.id]??""} onChange={e=>setSelectedCortes({...selectedCortes,[c.id]:Number(e.target.value)})}/><span>{currency}</span></label>)}</div>
          <div className="mt-3 flex justify-end">
            <Button
              disabled={!providerId || amount <= 0 || allocated <= 0 || allocated > amount || register.isPending}
              onClick={() => register.mutate()}
            >
              Guardar pago
            </Button>
          </div>
        </div>
      </section>

      <section className="overflow-x-auto rounded-xl border bg-card">
        <header className="border-b px-4 py-3">
          <h2 className="font-semibold">Historial de pagos ({history.data.payments.length})</h2>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Proveedor</th>
              <th className="px-3 py-2 text-right">Importe</th>
              <th className="px-3 py-2 text-right">Aplicado</th>
              <th className="px-3 py-2">Método</th>
              <th className="px-3 py-2">Referencia</th>
              <th className="px-3 py-2">Registró</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {history.data.payments.map((p: any) => (
              <tr key={p.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{p.paid_at}</td>
                <td className="px-3 py-2">{p.provider_name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(p.amount, p.currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(p.allocated, p.currency)}</td>
                <td className="px-3 py-2 text-xs">{p.method ?? "—"}</td>
                <td className="px-3 py-2 text-xs">{p.reference ?? "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{p.created_by_email ?? "—"}</td>
                <td className="px-3 py-2">
                  <Badge variant={p.voided_at ? "destructive" : "secondary"}>
                    {p.voided_at ? "Anulado" : "Aplicado"}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  {!p.voided_at ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm("¿Anular este pago? El saldo volverá a quedar pendiente."))
                          voidPay.mutate(p.id);
                      }}
                    >
                      Anular
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!history.data.payments.length ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  Aún no hay pagos registrados.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
